import DisplayAddress from '~spa/classes/DisplayAddress';
import DateRangeDaysDuration from '~spa/classes/DateRangeDaysDuration';
import Contact from '~spa/api/app/contact/models/Contact';
import BaseModel from '@spa/apps/trustup-pro-base-js/models/BaseModel';
import WorksiteProfitabilityDocumentCollection from '@spa/apps/trustup-pro-worksite-js/collections/WorksiteProfitabilityDocumentCollection';
import WorksitePhaseCollection from '@spa/apps/trustup-pro-worksite-js/collections/WorksitePhaseCollection';    

export default class Worksite extends BaseModel
{

    init(data)
    {
        super.init(data);

        this.starts_at = window.moment(this.starts_at).format('YYYY-MM-DD');
        this.ends_at = window.moment(this.ends_at).format('YYYY-MM-DD');
        this.full_contact = null;

        if ( this.profitability_documents ) {
            this.profitability_documents = new WorksiteProfitabilityDocumentCollection(this.profitability_documents, this);
        }

        if ( this.phases ) {
            this.phases = new WorksitePhaseCollection(this.phases, this);
        } 
    }

    static getPrefix()
    {
        return 'worksite:worksite';
    }

    static getEndpoint()
    {
        return window.Worksite.endpoints.worksite;
    }

    getRoute()
    {
        return {name: 'account.worksite.homepage', params: {
            account: window.App.$store.state.account.uuid,
            worksite: this.uuid
        }};
    }

    data() 
    {
        let data = {};

        for (let property in this.originalData) {
            data[property] = this[property];
        }

        if ( data.address == null ) {
            data.address = {};
        }

        if ( data.color == null ) {
            data.color = '#000000';
        }

        return data;
    }
    
    durationProgressPercentage()
    {
        return new DateRangeDaysDuration(this.starts_at, this.ends_at).getPercentage();
    }

    getAddress()
    {
        return new DisplayAddress(this.address).get();
    }

    getQuotes()
    {
        return this.profitability_documents.filter(d => d.profitability_type == 'quoted');
    }

    getInvoices()
    {
        return this.profitability_documents.filter(d => d.profitability_type == 'invoiced');
    }

    getCosts()
    {
        return this.profitability_documents.filter(d => d.profitability_type == 'costs');
    }

    getCostsOfType(type)
    {
        return this.profitability_documents.filter(d => d.profitability_type == 'costs' && d.type == type);
    }

    getMargin()
    {
        return this.getProfitability().total.margin;
    }

    refreshProfitability()
    {
        this.profitability = null;
        this.getProfitability();
    }

    getProfitability()
    {
        if ( this.profitability ) {
            return this.profitability;
        }

        let profitability = {
            documents: this.profitability_documents.get(),
            total: {
                quoted: this.getQuotes().reduce((a, b) => +a + +b.amount, 0),
                invoiced: this.getInvoices().reduce((a, b) => +a + +b.amount, 0),
                invoiced_paid: this.getInvoices().filter(d => d.status == 'paid').reduce((a, b) => +a + +b.amount, 0),
                costs: this.getCosts().reduce((a, b) => +a + +b.amount, 0),
                liquidity: 0,
                remaining_to_invoice: 0,
                margin: 0,
            }
        };

        profitability.total.remaining_to_invoice = profitability.total.quoted - profitability.total.invoiced;
        if ( profitability.total.remaining_to_invoice < 0 ) {
            profitability.total.remaining_to_invoice = 0;
        }
        
        profitability.total.liquidity = profitability.total.invoiced_paid - profitability.total.costs;
        profitability.total.margin = profitability.total.quoted - profitability.total.costs;

        this.profitability = profitability;

        return profitability;
    }

    hasPositiveMargin()
    {
        return this.getProfitability().total.margin > 0;
    }

    hasPositiveLiquidity()
    {
        return this.getProfitability().total.liquidity > 0;
    }

    isFullyInvoiced()
    {
        return this.getProfitability().total.invoiced >= this.getProfitability().total.quoted;
    }

    chartWidth(profitabilityDocument)
    {
        return (100 * profitabilityDocument.amount) / this.getProfitability().total.quoted;
    }

    chartColor(profitabilityDocument)
    {
        if ( profitabilityDocument.status == 'overdue' ) {
            return 'bg-red-500';
        }

        if ( profitabilityDocument.status == 'paid' ) {
            return 'bg-green-500';
        }

        if ( profitabilityDocument.status == 'sent' ) {
            return 'bg-blue-500';
        }

        return 'bg-gray-300'
    }

    chartWidthMultiple(profitabilityDocuments)
    {
        let width = 0;

        for ( let i = 0; i < profitabilityDocuments.length; i++ ) {
            width += this.chartWidth(profitabilityDocuments[i]);
        }

        return width;
    }

    chartCostsWidth()
    {
        return (100 * this.getProfitability().total.costs) / this.getProfitability().total.quoted;
    }

    chartWidthSubCosts(type)
    {
        return this.chartWidthMultiple(this.getCostsOfType(type));
    }

    chartWidthMultipleSubCosts(types)
    {
        let width = 0;

        for ( let i = 0; i < types.length; i++ ) {
            width += this.chartWidthMultiple(this.getCostsOfType(types[i]));
        }

        return width;
    }

    chartMarginWidth()
    {
        return (100 * this.getProfitability().total.margin) / this.getProfitability().total.quoted;
    }

    chartRemainingInvoiceWidth()
    {
        return (100 * (this.getProfitability().total.quoted - this.getProfitability().total.invoiced)) / this.getProfitability().total.quoted;
    }

    containsExternalProfitabilityDocument(uuid)
    {
        return this.profitability_documents.some(d => d.external_uuid == uuid);
    }

    containsUndefinedUsers()
    {
        return this.getUsersWithUndefined().length > this.getUsers().length;
    }

    getManagersWithUndefined()
    {
        return this.managers_id.map(id => {
            return window.App.$store.state.models.users.find(user => user.id == id);
        });
    }

    getManagers()
    {
        return this.getManagersWithUndefined().filter(u => u !== undefined);
    }

    getManagersName()
    {
        return this.getManagers().map(user => user.getDisplayName()).join(', ');
    }

    getUsersWithUndefined()
    {
        return this.users_id.map(id => {
            return window.App.$store.state.models.users.find(user => user.id == id);
        });
    }

    getUsers()
    {
        return this.getUsersWithUndefined().filter(u => u !== undefined);
    }

    getUsersName()
    {
        return this.getUsers().map(user => user.getDisplayName()).join(', ');
    }

    getContactName()
    {
        if ( this.contact ) {
            return this.contact.name;
        }
        if ( this.contact_uuid ) {
            return window.App.$t('worksite.contact.not_found');
        }
        return window.App.$t('worksite.contact.empty');
    }

    async getFullContact()
    {
        if ( ! this.contact ) {
            return;
        }

        if ( this.contact.full ) {
            return this.contact;
        }

        this.contact = await new Contact().show(this.contact_uuid);
        this.contact.full = true;
    }

    getContactAddress()
    {
        if ( ! this.contact ) {
            return null;
        }

        return new DisplayAddress({
            street_name: this.contact.address_line_1,
            street_number: this.contact.address_line_2,
            city: this.contact.city,
            postal_code: this.contact.postal_code,
        }).get();
    }

    isCurrent()
    {
        return window.moment().diff(window.moment(this.starts_at)) >= 0;
    }

    isFuture()
    {
        return ! this.isCurrent();
    }

};